import { useEffect, useState } from 'react'
import TableRow from '@mui/material/TableRow'
import AdvancedTable from 'components/AdvancedTable'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Checkbox, IconButton } from '@mui/material'
import useQueryGet from 'hooks/useQueryGet'
import type { ApiBarcode } from 'types/Barcode'
import Box from '@mui/material/Box'
import { AbilityContext } from 'config/ability'
import { type FieldValues, useForm, type SubmitHandler } from 'react-hook-form'
import { axiosPost } from 'connectors/axiosPost'
import { useAlert } from 'context/AlertContext'
import ConfirmDialog from 'components/ConfirmDialog'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import { renderCells } from 'utils/renderCells'
import { EditCell } from 'components/ActionDropdown/ActionDropdown'
import { useAbility } from '@casl/react'
import { TrashIcon } from 'assets/icons/trash'
import { LabelBox } from 'components/label-box'
import { Market } from 'types/Market'
import { useDebounce } from 'hooks/use-debounce'
import PageLoader from 'components/PageLoader'

type BarcodesProps = {
  market?: Market
  selectedMarketId: string
  search: string
}

const Barcodes = ({ market, search }: BarcodesProps): JSX.Element => {
  const queryClient = useQueryClient()
  // const [isDeleteMode, setIsDeleteMode] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<null | ApiBarcode>(null)
  const { t } = useTranslation()
  const ability = useAbility(AbilityContext)
  const { token, refreshToken } = useAuthContext()
  const { changeMessage } = useAlert()
  const debouncedSearch = useDebounce(search, 300)
  const [dataToRemove, setDataToRemove] = useState<{ marketId?: string; ids?: Array<string | number> } | null>(null)

  const marketFilter = market && market.id !== -1 && !!market?.id ? `marketId=${market.id}` : ''

  type SelectedValues = {
    barcodes: string[]
  } & FieldValues
  const methods = useForm<SelectedValues>({ defaultValues: { barcodes: [] } })

  const {
    mutate: deleteMutate,
    data: deleteResponse,
    reset,
    isLoading,
  } = useMutation(
    async ({ ids, marketId }: { marketId?: string; ids?: Array<string | number> }) =>
      await axiosPost<string>(token, refreshToken, 'barcodes/delete', { barcodes: ids, marketId }),
    {
      onSuccess: async () => {
        methods.reset()
        void queryClient.refetchQueries(['barcodes-query'])
      },
    }
  )

  const isSpecificMarket = Boolean(market && market.id !== -1)

  // Liczba barkodów wybranego rynku (bez listy) — do potwierdzenia i wyłączenia
  // przycisku, gdy nie ma czego usuwać. Odświeża się po usunięciu (deleteResponse).
  const { data: countData } = useQueryGet<{ meta: { itemCount: number } }>({
    queryKey: ['barcodes-count', market?.id, deleteResponse],
    endpoint: `barcodes?marketId=${market?.id}`,
    options: { params: { take: 1 } },
    queryOptions: { enabled: isSpecificMarket },
    refetchDeps: [market?.id, deleteResponse],
  })
  const barcodesCount =
    countData && typeof countData === 'object' && 'meta' in countData ? countData.meta.itemCount : undefined

  const handleDelete = (): void => {
    if (dataToRemove !== null) {
      deleteMutate(dataToRemove)
    }
    setDataToRemove(null)
  }

  const onSubmit: SubmitHandler<SelectedValues> = data => {
    if (data.barcodes.length === 0 && !!market) {
      // setIsDeleteMode(true)
      setDataToRemove({ marketId: `${market.id}` })
    } else {
      setDataToRemove({ ids: data.barcodes })
    }
  }

  useEffect(() => {
    if (deleteResponse !== undefined && typeof deleteResponse !== 'string' && 'error' in deleteResponse) {
      changeMessage(deleteResponse?.error?.data?.message, 'error', reset)
    }
    if (deleteResponse === '') {
      changeMessage(t('common.success'), 'success', reset)
      setItemToDelete(null)
    }
    queryClient.refetchQueries({
      queryKey: [`barcodes?${marketFilter}${debouncedSearch ? '&code=' + debouncedSearch : ''}-query`],
      exact: false,
    })
  }, [deleteResponse])

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mb={4}>
        {isSpecificMarket && ability.can('delete', 'Barcodes') && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<TrashIcon />}
            disabled={!barcodesCount}
            onClick={() => {
              if (market) setDataToRemove({ marketId: `${market.id}` })
            }}
          >
            Delete all barcodes for market{typeof barcodesCount === 'number' ? ` (${barcodesCount})` : ''}
          </Button>
        )}
      </Box>
      <AdvancedTable<ApiBarcode>
        name={`barcodes?${marketFilter}${debouncedSearch ? '&code=' + debouncedSearch : ''}`}
        endpoint={`barcodes?${marketFilter}${debouncedSearch ? '&code=' + debouncedSearch : ''}`}
        formId="barcodes-table-form"
        formHandler={methods.handleSubmit(onSubmit)}
        // isExportMode={isDeleteMode}
        // exportText={t('common.delete')}
        disableFilters
        headings={[
          { type: 'empty', name: '', field: 'id-selector', width: 74 },
          { type: 'empty', name: 'ID', field: 'id', sort: true, width: 90 },
          { type: 'text', name: t('common.code'), field: 'code' },
          { type: 'empty', name: t('common.marketName'), field: 'marketId', width: 150 },
          { type: 'empty', name: '', field: 'actions', width: 120, hide: !ability.can('delete', 'Barcodes') },
        ]}
        options={{}}
        renderRow={row => (
          <TableRow key={row.id}>
            {renderCells([
              { label: '', value: <Checkbox value={row.id} {...methods.register('barcodes')} /> },
              { label: 'ID', value: <LabelBox first={`#${row.id}`} /> },
              {
                label: t('common.code'),
                value: <LabelBox first={row.code} />,
              },
              { label: t('common.marketName'), value: <LabelBox first={row.market.name} /> },
            ])}
            {ability.can('delete', 'Barcodes') && (
              <EditCell>
                <IconButton
                  onClick={() => {
                    setItemToDelete(row)
                  }}
                >
                  <TrashIcon />
                </IconButton>
              </EditCell>
            )}
          </TableRow>
        )}
      />
      <ConfirmDialog
        open={dataToRemove !== null}
        onAgree={handleDelete}
        onDisagree={() => {
          setDataToRemove(null)
        }}
        confirmText="Delete"
        question={
          dataToRemove?.marketId !== undefined
            ? `Are you sure you want to delete all ${barcodesCount ?? ''} barcodes for ${market?.name}${
                market?.description ? ' (' + market.description + ')' : ''
              }? This action cannot be undone.`
            : t('dialogs.delete')
        }
      />
      <ConfirmDialog
        open={!!itemToDelete}
        onAgree={() => {
          if (itemToDelete) {
            deleteMutate({ ids: [itemToDelete.id] })
          }
        }}
        onDisagree={() => {
          setItemToDelete(null)
        }}
        question={t('dialogs.delete', { name: itemToDelete?.code })}
        confirmText={t('common.delete')}
      />
      {isLoading && <PageLoader />}
    </Box>
  )
}

export default Barcodes
